import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useAuthStore } from '../stores/authStore'
import i18n from '../i18n'

const API_BASE_URL = '/api'

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = useAuthStore.getState().token
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`
    }
    // Send the active UI language so the backend can return localized content
    // (matched against {en,hi,mr} by LanguageResolver, defaulting to en).
    if (config.headers) {
      config.headers['Accept-Language'] = i18n.language?.split('-')[0] || 'en'
    }
    return config
  },
  (error: AxiosError) => {
    return Promise.reject(error)
  }
)

// --- Access-token expiry detection -------------------------------------------
// The backend's JwtAuthenticationFilter silently skips an expired access token
// (validateToken() catches ExpiredJwtException and returns false → no
// SecurityContext is set → the request is anonymous). A protected /admin/**
// endpoint then rejects the anonymous request with 403 (NOT 401 — there's no
// AuthenticationEntryPoint returning 401). So an expired access token surfaces
// as a 403, which the old 401-only interceptor never handled — every guarded
// call on a stale session failed silently, leaving the admin dashboard showing
// 0/empty while public calls (e.g. /pages/popular) still worked.
//
// To recover transparently, we decode the JWT `exp` and, when a 403 is due to
// an expired access token, refresh it once via /auth/refresh and retry.
function isAccessTokenExpired(token: string | null): boolean {
  if (!token) return true
  try {
    const payload = token.split('.')[1] || ''
    const decoded = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
    )
    // `exp` is seconds-since-epoch; compare against now in ms.
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 <= Date.now() : false
  } catch {
    return false
  }
}

// A single in-flight refresh promise so concurrent failing requests (the
// dashboard fires several guarded calls in parallel) share ONE refresh
// round-trip. The backend revokes the old refresh token on use, so racing
// parallel refreshes would otherwise see the second one fail with "Invalid
// refresh token" and log the user out.
let refreshing: Promise<string | null> | null = null

async function refreshAccessToken(): Promise<string | null> {
  if (refreshing) return refreshing
  const { refreshToken, setToken, setRefreshToken, logout } = useAuthStore.getState()
  if (!refreshToken) return null
  refreshing = (async () => {
    try {
      // Use the bare `axios` (not the `api` instance) so this call bypasses
      // both interceptors — no stale-token header, and no recursion into this
      // response interceptor if the refresh itself fails.
      const res = await axios.post('/api/auth/refresh', { refreshToken })
      const data = res.data?.data
      if (!data?.accessToken) return null
      setToken(data.accessToken)
      setRefreshToken(data.refreshToken)
      return data.accessToken as string
    } catch {
      logout()
      window.location.href = '/login'
      return null
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined
    const status = error.response?.status

    // 401 = genuine auth failure (bad credentials / invalid refresh token).
    // The expired-access-token case is a 403, handled below — so a 401 here
    // means we cannot recover: log out and send the user to login.
    if (status === 401) {
      useAuthStore.getState().logout()
      window.location.href = '/login'
      return Promise.reject(error)
    }

    // 403 + expired access token = the silent-expiry case. Refresh once and
    // retry. A 403 with a still-valid token is a genuine authorization denial
    // (wrong role / forbidden resource) — propagate it as-is; do NOT refresh.
    if (
      status === 403 &&
      originalRequest &&
      !originalRequest._retry &&
      isAccessTokenExpired(useAuthStore.getState().token)
    ) {
      originalRequest._retry = true
      const newToken = await refreshAccessToken()
      if (newToken) {
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`
        }
        try {
          return api.request(originalRequest)
        } catch (e) {
          // Retry still failed — give up the session.
          useAuthStore.getState().logout()
          window.location.href = '/login'
          return Promise.reject(e)
        }
      }
      // No refresh token / refresh failed (refreshAccessToken already
      // redirected on failure). Ensure we don't leave a stale session.
      useAuthStore.getState().logout()
      window.location.href = '/login'
      return Promise.reject(error)
    }

    // Handle 403 Forbidden (genuine, non-expiry)
    if (error.response?.status === 403) {
      console.error('Access denied')
    }

    // Handle 500 Server Error
    if (error.response?.status && error.response.status >= 500) {
      console.error('Server error')
    }

    return Promise.reject(error)
  }
)

/**
 * Per-call timeout (ms) for Multipart uploads. The default 30 s axios timeout
 * is far too short for a 100 MB upload on a slow link, so every upload call
 * overrides the instance timeout with this value (10 min).
 */
export const UPLOAD_TIMEOUT = 600000

/**
 * Pull the exact, user-facing error message out of an axios error so upload
 * failures show WHY they failed (size limit, corrupt video, ffprobe reason…)
 * instead of a generic "Failed… try again" string.
 *
 * Backend `GlobalExceptionHandler` returns `{ success:false, message:"…" }`,
 * so the real reason lives at `error.response.data.message`. We also cover
 * nginx's 413 (which returns an HTML body, not our JSON shape) and the
 * browser-side upload timeout (ECONNABORTED) with clear wording.
 */
export function getApiErrorMessage(e: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const err = e as any
  // Axios timeout / network abort (large-upload took longer than the per-call timeout)
  if (err?.code === 'ECONNABORTED') {
    return 'Upload timed out — the file is too large or the network is slow. Try a smaller file or a stronger connection.'
  }
  const data = err?.response?.data
  // Our ApiResponse JSON shape: { success, message, ... }
  if (data && typeof data === 'object') {
    const m = data.message
    if (typeof m === 'string' && m.trim()) return m
    const e2 = data.error
    if (typeof e2 === 'string' && e2.trim()) return e2
    // Some validation responses nest errors under `data`
    if (data.data && typeof data.data === 'object') {
      const dm = data.data.message
      if (typeof dm === 'string' && dm.trim()) return dm
    }
  }
  // nginx 413 / non-JSON upstream bodies
  if (typeof data === 'string' && data.trim()) {
    const stripped = data.trim()
    if (stripped.length < 200) return stripped
  }
  if (err?.response?.status === 413) {
    return 'File too large. The maximum allowed upload is 20 MB. Please upload a smaller file.'
  }
  if (typeof err?.message === 'string' && err.message.trim()) return err.message
  return fallback
}

export default api